import {merge} from 'lodash';

import {reportToBugsnag} from '../../../../../srcCommon/services/bugsnag/bugsnag';
import {mapTermsToFilters} from '../../../../analytics/helpers/analyticsSavedViewsHelpers';
import {makeAnalyticsSelectedViewUrl} from '../../../../analytics/helpers/analyticsUrlHelpers';
import {getAnalyticsTrackingData} from '../../../../analytics/helpers/tracking/analyticsSelectors';
import {CancelError, CancelToken} from '../../../../helpers/asyncCancelHelpers';
import {computeIsGlobalNamespace} from '../../../../helpers/models/namespaceHelpers';
import {
  AnalyticsFilterRelationsEnum,
  AnalyticsFilterTypesEnum,
  AnalyticsSavedViewModel,
  AnalyticsSavedViewTerms,
  ApiAnalyticsSavedView,
  ApiAnalyticsSavedViews,
  serializeAnalyticsSavedView,
  serializeBaseAnalyticsSavedView,
} from '../../../../models/api/analyticsSavedViewModel';
import {deleteAsync, fetchApiAsync, postApiAsync, putApiAsync} from '../../../../services/http/http';
import {HttpError, HttpResponseError} from '../../../../services/http/httpError';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {AppState} from '../../../appState';
import {ThunkAction, ThunkAsyncAction} from '../../../appThunk';
import {getCompany} from '../../../data/company/companySelectors';
import {
  setAnalyticsViewPinnedAction,
  updateAnalyticsDefaultViewsAction,
} from '../../../data/preferences/preferencesActions';
import {
  getAnalyticsDefaultViews,
  getAnalyticsPinnedViews,
} from '../../../data/preferences/preferencesSelectors';
import {navigateToLinkAction} from '../../../stateless/navigationActions';
import {attachTagModeToOptions, makeAnalyticsApiPath} from '../analyticsPanelHelpers';
import {getUnsavedViewNavigationBlockerId} from '../analyticsPanelSelectors';
import {updateFiltersAction} from '../filters/analyticsPanelFiltersActions';
import {DEFAULT_FILTERS_STATE} from '../filters/analyticsPanelFiltersState';
import {
  getAnalyticsParameters,
  getAnalyticsSelectedNamespace,
} from '../parameters/analyticsPanelParametersSelectors';
import {setAnalyticsReportOptions} from '../report/analyticsPanelReportActions';
import {getAnalyticsReportOptions} from '../report/analyticsPanelReportSelectors';
import {
  AddTemporaryViewAction,
  AnalyticsPanelViewsActionsEnum,
  CreateSavedViewAction,
  DeleteSavedViewAction,
  FetchSavedViewsAction,
  FetchSavedViewsCompletedAction,
  FetchSavedViewsFailedAction,
  UpdateSavedViewAction,
} from './analyticsPanelViewsActionsTypes';
import {
  getAnalyticsNbTemporaryViews,
  getAnalyticsSavedViews,
  getAnalyticsTemporaryViews,
  makeGetSelectedView,
} from './analyticsPanelViewsSelectors';
import {
  ALL_DATA_ID,
  AnalyticsSavedView,
  AnalyticsSelectedViewId,
  isAnalyticsViewSaved,
  isSelectedViewAllData,
} from './analyticsPanelViewsState';

/*
 * Constants.
 */

const baseTerms: AnalyticsSavedViewTerms = [
  {
    type: AnalyticsFilterTypesEnum.INBOXES,
    special_value: DEFAULT_FILTERS_STATE.inboxes,
  },
  {
    type: AnalyticsFilterTypesEnum.TEAMMATES,
    negated: false,
    ids: [],
    relation: AnalyticsFilterRelationsEnum.OR,
  },
];

const teamsTerm = {
  type: AnalyticsFilterTypesEnum.TEAMS,
  negated: false,
  ids: [],
  relation: AnalyticsFilterRelationsEnum.OR,
};

/*
 * Action creators.
 */

export function setSelectedViewAction(selectedViewId: AnalyticsSelectedViewId): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    if (isSelectedViewAllData(selectedViewId)) {
      // If user selected `All data`, then we should reset the filters and report options.
      dispatch(setAnalyticsReportOptions({}));
      dispatch(updateFiltersAction(DEFAULT_FILTERS_STATE));
    } else {
      // Otherwise, we need to retrieve terms of this view, and map it to filters.
      const selectedView = makeGetSelectedView(selectedViewId)(state);
      if (!selectedView) {
        return;
      }

      // If we try to select a view that isn't displayed, we toggle the visibility ON.
      if (!selectedView.isDisplayed) {
        dispatch(toggleViewVisibility(selectedViewId));
      }

      // Update the options (selected metrics + bucket boundaries) on the report.
      dispatch(setAnalyticsReportOptions(selectedView.filters.options || {}));

      // Settings filters in store.
      dispatch(updateFiltersAction(mapTermsToFilters(selectedView.filters)));
    }
  };
}

export function toggleViewVisibility(viewId: AnalyticsSelectedViewId): ThunkAction {
  return (dispatch, getState) => {
    dispatch({
      type: AnalyticsPanelViewsActionsEnum.TOGGLE_VIEW_VISIBILITY,
      payload: viewId,
    });

    const selectedView = makeGetSelectedView(viewId)(getState());
    if (selectedView?.isDisplayed) {
      // Selecting the view we now display.
      return dispatch(setSelectedViewAction(viewId));
    }

    return undefined;
  };
}

export function addTemporaryViewAction(viewId: string): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const temporaryViews = getAnalyticsTemporaryViews(state);
    // We need to keep track of the number of tmp views created separately to avoid conflicts when closing a tmp view.
    const nbTemporaryViews = getAnalyticsNbTemporaryViews(state);
    const temporaryViewsCopy = temporaryViews.slice();

    const selectedNamespace = getAnalyticsSelectedNamespace(state);

    // We only include the teams filter if currently in a global namespace.
    const terms = computeIsGlobalNamespace(selectedNamespace) ? [teamsTerm, ...baseTerms] : baseTerms;

    const tmpView: AnalyticsSavedView = {
      id: viewId,
      name: `New View (${nbTemporaryViews + 1})`,
      filters: {
        terms,
      },
      isPinned: false,
      isDisplayed: true,
      namespace: selectedNamespace,
      visibilityNamespace: undefined,
      metadata: {
        tags: {},
        fullTags: {},
        inboxes: {},
        accounts: {},
        hasAllInboxes: true,
      },
      isDefault: false,
      schedulingConfig: null,
    };
    temporaryViewsCopy.push(tmpView);
    dispatch<AddTemporaryViewAction>({
      type: AnalyticsPanelViewsActionsEnum.ADD_TEMPORARY_VIEW,
      payload: temporaryViewsCopy,
    });

    return undefined;
  };
}

export function fetchFullSavedViewAsyncAction(
  viewId: AnalyticsSelectedViewId,
  cancelToken?: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);

    const path = `${getAnalyticsSavedViewsApiPath(company.url)}/${viewId}/full`;

    try {
      // Initiate a fetch from the network.
      const fetchSavedViewPromise = fetchApiAsync(ApiAnalyticsSavedView, path, {
        cancelToken,
      });
      const savedView = await fetchSavedViewPromise;

      const previousSavedViews = getAnalyticsSavedViews(state);

      const formattedSavedView = formatSavedView(savedView, state, previousSavedViews);

      dispatch<UpdateSavedViewAction>({
        type: AnalyticsPanelViewsActionsEnum.UPDATE_SAVED_VIEW,
        payload: formattedSavedView,
      });
    } catch (error) {
      // If this is a cancellation error, do nothing.
      if (error instanceof CancelError) {
        return;
      }

      // We don't expect errors other than HttpErrors.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
    }
  };
}

export function fetchInitialSavedViewsAsyncAction(
  selectedNamespace: string,
  toViewId?: AnalyticsSelectedViewId,
  cancelToken?: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const parameters = getAnalyticsParameters(state);
    const ignoreUnsavedViewNavigationBlockerId = getUnsavedViewNavigationBlockerId(state);

    const formattedSavedViews = await dispatch(fetchSavedViewsAsyncAction(selectedNamespace, cancelToken));

    // After fetching the views for the current namespace, we potentially need to navigate to
    // select an appropriate saved view, or the included toViewId.
    const idToSelect = computeIdToSelect(toViewId, formattedSavedViews ?? []);
    const pathToNavigate = makeAnalyticsSelectedViewUrl(idToSelect, parameters);

    // Navigation after fetching saved views should not be blockable.
    await dispatch(navigateToLinkAction(pathToNavigate, undefined, ignoreUnsavedViewNavigationBlockerId));
    dispatch(setSelectedViewAction(idToSelect));
  };
}

function fetchSavedViewsAsyncAction(
  selectedNamespace: string,
  cancelToken?: CancelToken,
): ThunkAsyncAction<ReadonlyArray<AnalyticsSavedView> | undefined> {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);

    const path = getAnalyticsSavedViewsApiPath(company.url, selectedNamespace);

    dispatch<FetchSavedViewsAction>({
      type: AnalyticsPanelViewsActionsEnum.FETCH_SAVED_VIEWS,
    });

    try {
      // Initiate a fetch from the network.
      const fetchSavedViewsPromise = fetchApiAsync(ApiAnalyticsSavedViews, path, {cancelToken});
      const savedViews = await fetchSavedViewsPromise;
      const previousSavedViews = getAnalyticsSavedViews(state);

      const formattedSavedViews = savedViews.map((savedView) =>
        formatSavedView(savedView, state, previousSavedViews),
      );

      dispatch<FetchSavedViewsCompletedAction>({
        type: AnalyticsPanelViewsActionsEnum.FETCH_SAVED_VIEWS_COMPLETED,
        payload: formattedSavedViews,
      });

      return formattedSavedViews;
    } catch (error) {
      // If this is a cancellation error, do nothing.
      if (error instanceof CancelError) {
        return undefined;
      }

      // We don't expect errors other than HttpErrors.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      // Otherwise, dispatch the failed action.
      dispatch<FetchSavedViewsFailedAction>({
        type: AnalyticsPanelViewsActionsEnum.FETCH_SAVED_VIEWS_FAILED,
        payload: {
          error,
        },
      });

      return undefined;
    }
  };
}

export function createSavedViewAsyncAction(
  view: Omit<AnalyticsSavedView, 'id' | 'isDisplayed' | 'metadata'>,
  namespace: string,
  selectedTemporaryViewId?: AnalyticsSelectedViewId,
): ThunkAsyncAction<number | undefined> {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);
    const trackingData = getAnalyticsTrackingData(state);

    // We need to attach the selected metrics and tag mode to the payload.
    const viewWithOptions = attachOptionsToView(state, view);

    const serializedView = serializeBaseAnalyticsSavedView(viewWithOptions, namespace);

    const path = getAnalyticsSavedViewsApiPath(company.url, namespace);

    dispatch<CreateSavedViewAction>({
      type: AnalyticsPanelViewsActionsEnum.CREATE_SAVED_VIEW,
      payload: viewWithOptions,
    });

    try {
      const {isPinned, isDefault} = view;
      // Create the new view.
      const createSavedViewPromise = postApiAsync(ApiAnalyticsSavedView, path, serializedView);
      const createdView = await createSavedViewPromise;

      trackInterfaceEvent(InterfaceEventsEnum.ANALYTICS_VIEW_CREATE, {
        ...trackingData,
        saved_view_id: createdView.id,
        is_default: isDefault,
        is_pinned: isPinned,
      });

      if (isDefault) {
        // Set view as default.
        dispatch(updateAnalyticsDefaultViewsAction(createdView.visibilityNamespace, createdView.id));
      }

      if (isPinned) {
        // Pin view.
        dispatch(setAnalyticsViewPinnedAction(createdView.id, true));
      }

      // Fetch views.
      await dispatch(fetchSavedViewsAsyncAction(createdView.visibilityNamespace));

      if (selectedTemporaryViewId) {
        // This means we turned a temporary view into a saved view - need to remove the tmp view.
        dispatch({
          type: AnalyticsPanelViewsActionsEnum.REMOVE_TEMPORARY_VIEW,
          payload: selectedTemporaryViewId,
        });
      }

      return createdView.id;
    } catch (error) {
      if (error instanceof HttpResponseError && error.status === 409) {
        dispatch({
          type: AnalyticsPanelViewsActionsEnum.CREATE_SAVED_VIEW_FAILED,
          payload: error.status,
        });
        return undefined;
      }
      // If this is a cancellation error, do nothing.
      if (error instanceof CancelError) {
        return undefined;
      }

      // We don't expect errors other than HttpErrors.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
        return undefined;
      }
      return undefined;
    }
  };
}

export function updatedSavedViewIsPinnedAction(
  view: AnalyticsSavedView,
  isPinned: boolean,
): ThunkAction<UpdateSavedViewAction> {
  return (dispatch) => {
    if (isAnalyticsViewSaved(view.id)) {
      // Update preferences.
      dispatch(setAnalyticsViewPinnedAction(view.id, isPinned));
    }

    // Update view.
    return dispatch<UpdateSavedViewAction>({
      type: AnalyticsPanelViewsActionsEnum.UPDATE_SAVED_VIEW,
      payload: {
        ...view,
        isPinned,
        isDisplayed: view.isDisplayed || isPinned,
      },
    });
  };
}

export function updateSavedViewAsyncAction(
  view: AnalyticsSavedView,
  namespace: string,
): ThunkAsyncAction<number | undefined> {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);
    const path = getAnalyticsSavedViewsApiPath(company.url);

    // We need to attach the selected metrics and the tag mode to the payload.
    const viewWithOptions = attachOptionsToView(state, view);

    dispatch<UpdateSavedViewAction>({
      type: AnalyticsPanelViewsActionsEnum.UPDATE_SAVED_VIEW,
      payload: viewWithOptions,
    });

    try {
      const {isPinned, isDefault} = viewWithOptions;

      // Update the view we want to make default.
      const serializedView = serializeAnalyticsSavedView(viewWithOptions, namespace);
      const {id, ...params} = serializedView;
      await putApiAsync(ApiAnalyticsSavedView, `${path}/${id}`, params);

      // Set pin status.
      dispatch(setAnalyticsViewPinnedAction(id, isPinned));

      // Set default view status.
      dispatch(maybeChangeDefaultView(state, serializedView.visibility_namespace, id, isDefault));

      // Fetch views.
      dispatch(fetchSavedViewsAsyncAction(serializedView.visibility_namespace));

      return id;
    } catch (error) {
      // We don't expect errors other than HttpErrors.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      return undefined;
    }
  };
}

export function deleteSavedViewAsyncAction(viewId: AnalyticsSelectedViewId): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);
    const selectedNamespace = getAnalyticsSelectedNamespace(state);
    const parameters = getAnalyticsParameters(state);
    const ignoreUnsavedViewNavigationBlockerId = getUnsavedViewNavigationBlockerId(state);

    if (!isAnalyticsViewSaved(viewId)) {
      return;
    }

    const path = getAnalyticsSavedViewsApiPath(company.url);

    dispatch<DeleteSavedViewAction>({
      type: AnalyticsPanelViewsActionsEnum.DELETE_SAVED_VIEW,
      payload: viewId,
    });

    try {
      // Initiate a fetch from the network.
      const deleteSavedViewPromise = deleteAsync(`${path}/${viewId}`);
      await deleteSavedViewPromise;

      // After deleting the view we need to navigate back to the all data view. This
      // navigation should not be blockable.
      const pathToNavigate = makeAnalyticsSelectedViewUrl(ALL_DATA_ID, parameters);
      await dispatch(navigateToLinkAction(pathToNavigate, undefined, ignoreUnsavedViewNavigationBlockerId));
      dispatch(setSelectedViewAction(ALL_DATA_ID));

      // Update pins and default views
      dispatch(setAnalyticsViewPinnedAction(viewId, false));
      dispatch(maybeChangeDefaultView(state, selectedNamespace, viewId, false));

      // Fetch views.
      dispatch(fetchSavedViewsAsyncAction(selectedNamespace));
    } catch (error) {
      // If this is a cancellation error, do nothing.
      if (error instanceof CancelError) {
        return;
      }

      // We don't expect errors other than HttpErrors.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
    }
  };
}

/*
 * Helpers.
 */

// Constructs API path
function getAnalyticsSavedViewsApiPath(companyUrl: string, selectedNamespace?: string) {
  return `${makeAnalyticsApiPath(companyUrl, selectedNamespace)}/views`;
}

function maybeChangeDefaultView(
  state: AppState,
  visibilityNamespace: string,
  viewId: number,
  isDefault: boolean,
): ThunkAction {
  return (dispatch) => {
    const currentDefaultViewId = getAnalyticsDefaultViews(state)[visibilityNamespace];

    // Unset default view if the current default view is set as non-default.
    if (!isDefault && currentDefaultViewId === viewId) {
      return dispatch(updateAnalyticsDefaultViewsAction(visibilityNamespace, undefined));
    }

    if (isDefault) {
      // Set new default view.
      return dispatch(updateAnalyticsDefaultViewsAction(visibilityNamespace, viewId));
    }

    return null;
  };
}

function attachOptionsToView<
  T extends AnalyticsSavedView | Omit<AnalyticsSavedView, 'id' | 'isDisplayed' | 'metadata'>,
>(state: AppState, view: T): T {
  const reportCurrentOptions = getAnalyticsReportOptions(state);

  const options = attachTagModeToOptions(state, reportCurrentOptions);

  return merge({}, view, {filters: {options}});
}

function formatSavedView(
  savedView: AnalyticsSavedViewModel,
  state: AppState,
  previousSavedViews: ReadonlyArray<AnalyticsSavedView>,
): AnalyticsSavedView {
  const {id, name, namespace, filters, metadata, visibilityNamespace, schedulingConfig} = savedView;

  const previousView = previousSavedViews.find((view) => view.id === id);

  const isPinned = getAnalyticsPinnedViews(state).includes(id);
  const isDefault = getAnalyticsDefaultViews(state)[savedView.visibilityNamespace] === id;

  const isDisplayed = previousView?.isDisplayed || isPinned;

  return {
    id,
    name,
    isDefault,
    filters,
    isPinned,
    isDisplayed,
    metadata,
    namespace,
    visibilityNamespace,
    schedulingConfig,
  };
}

/**
 * Compute the ID for us to select after fetching the saved views.
 */
const computeIdToSelect = (
  toViewId: AnalyticsSelectedViewId,
  formattedSavedViews: ReadonlyArray<AnalyticsSavedView>,
) => {
  const toView = formattedSavedViews.find((view) => view.id === toViewId);

  // If there is already a toViewId, and the user has access to that view, use that.
  if (toViewId && toView) {
    return toViewId;
  }

  const defaultViewId: AnalyticsSelectedViewId = formattedSavedViews.find((v) => v.isDefault)?.id;

  // If there is a default view among the views we loaded, select that one.
  if (defaultViewId) {
    return defaultViewId;
  }

  // Otherwise, select our default.
  return ALL_DATA_ID;
};
